import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, ArrowLeft, MessageSquare, Search, X,
  LogOut, UserPlus, FolderPlus, Folder, FolderOpen, MoreHorizontal,
} from 'lucide-react';
import { API, apiHeaders } from './chatUtils';

export default function ChatSidebar({
  sessions, activeSession, sidebarOpen, setSidebarOpen,
  folders, onNewChat, onSelectSession, onDeleteSession, onLogout,
  isGuest, user, onRefreshSessions, onRefreshFolders,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showFolderInput, setShowFolderInput] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults(null); return; }
    try {
      const res = await fetch(`${API}/api/chat/search?q=${encodeURIComponent(q)}`, { headers: apiHeaders() });
      if (res.ok) setSearchResults(await res.json());
    } catch { /* ignore */ }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await fetch(`${API}/api/chat/folders`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ name: newFolderName.trim() }),
      });
      setNewFolderName('');
      setShowFolderInput(false);
      onRefreshFolders();
    } catch { /* ignore */ }
  };

  const handleDeleteFolder = async (e, folderId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/api/chat/folders/${folderId}`, { method: 'DELETE', headers: apiHeaders() });
      onRefreshFolders();
      onRefreshSessions();
    } catch { /* ignore */ }
  };

  const handleMoveToFolder = async (e, sessionId, folderId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/api/chat/sessions/${sessionId}/folder?folder_id=${folderId}`, {
        method: 'PUT', headers: apiHeaders(),
      });
      onRefreshSessions();
    } catch { /* ignore */ }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const displaySessions = searchResults !== null ? searchResults : sessions;
  const unfolderedSessions = displaySessions.filter(s => !s.folder_id);
  const folderMap = {};
  folders.forEach(f => { folderMap[f.folder_id] = []; });
  displaySessions.forEach(s => {
    if (s.folder_id && folderMap[s.folder_id]) folderMap[s.folder_id].push(s);
  });

  const SessionItem = ({ session }) => (
    <button onClick={() => onSelectSession(session)}
      className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-[13px] transition-all duration-200 group active:scale-[0.98]
        ${activeSession?.session_id === session.session_id
          ? 'bg-overlay-strong text-content'
          : 'text-content-2 hover:bg-overlay-hover hover:text-content'}`}
      data-testid={`session-${session.session_id}`}>
      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-40" strokeWidth={1.5} />
      <span className="truncate flex-1 text-left font-sora">{session.title || 'New Chat'}</span>
      <Trash2 className="w-3 h-3 opacity-0 group-hover:opacity-40 hover:!opacity-100 hover:text-red-400 flex-shrink-0 transition-all"
        onClick={(e) => onDeleteSession(e, session.session_id)} data-testid={`delete-session-${session.session_id}`} />
    </button>
  );

  return (
    <aside className={`
      fixed md:relative z-40 h-full
      ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden'}
      w-[280px] md:w-[260px]
      transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
      border-r border-line bg-app flex flex-col
    `} data-testid="chat-sidebar">
      <div className="p-3 flex flex-col h-full gap-2">
        {/* New Chat */}
        <button onClick={onNewChat}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-line bg-overlay hover:bg-overlay-hover hover:border-line-2 text-[13px] transition-all duration-200 group active:scale-[0.98]"
          data-testid="new-chat-btn">
          <Plus className="w-4 h-4 text-emerald-500 group-hover:rotate-90 transition-transform duration-300" strokeWidth={1.5} />
          <span className="text-content-2 group-hover:text-content transition-colors font-sora font-medium">New Chat</span>
        </button>

        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-line bg-overlay">
          <Search className="w-3.5 h-3.5 text-content-3" strokeWidth={1.5} />
          <input value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search chats..."
            className="flex-1 bg-transparent text-[13px] outline-none text-content-2 placeholder-content-3 font-sora"
            data-testid="search-chats-input" />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} className="text-content-3 hover:text-red-400 transition-colors">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto space-y-0.5 scrollbar-thin overscroll-contain pr-1">
          {/* Folders */}
          {!searchResults && folders.map(folder => (
            <div key={folder.folder_id}>
              <button onClick={() => toggleFolder(folder.folder_id)}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-[13px] transition-all group text-content-2 hover:bg-overlay-hover"
                data-testid={`folder-${folder.folder_id}`}>
                {expandedFolders[folder.folder_id]
                  ? <FolderOpen className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} />
                  : <Folder className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} />
                }
                <span className="truncate flex-1 text-left font-sora font-medium">{folder.name}</span>
                <span className="text-[10px] text-content-3 font-mono">{folderMap[folder.folder_id]?.length || 0}</span>
                <Trash2 className="w-3 h-3 opacity-0 group-hover:opacity-40 hover:!opacity-100 hover:text-red-400 flex-shrink-0 transition-all"
                  onClick={(e) => handleDeleteFolder(e, folder.folder_id)} />
              </button>
              {expandedFolders[folder.folder_id] && (
                <div className="pl-3 space-y-0.5">
                  {(folderMap[folder.folder_id] || []).map(session => (
                    <SessionItem key={session.session_id} session={session} />
                  ))}
                </div>
              )}
            </div>
          ))}

          {unfolderedSessions.map(session => (
            <SessionItem key={session.session_id} session={session} />
          ))}
        </div>

        {/* New Folder */}
        {!isGuest && (
          <div>
            {showFolderInput ? (
              <div className="flex items-center gap-1 px-2 py-2 rounded-xl border border-line">
                <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  placeholder="Folder name" autoFocus
                  className="flex-1 bg-transparent text-[13px] outline-none text-content-2 font-sora"
                  data-testid="folder-name-input" />
                <button onClick={handleCreateFolder} className="text-emerald-500 text-xs font-medium font-sora">Add</button>
                <button onClick={() => { setShowFolderInput(false); setNewFolderName(''); }} className="text-content-3 text-xs">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowFolderInput(true)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-[13px] text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-all font-sora"
                data-testid="new-folder-btn">
                <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> New Folder
              </button>
            )}
          </div>
        )}

        {/* Bottom Actions */}
        <div className="pt-2 border-t border-line space-y-1">
          {isGuest ? (
            <button onClick={() => navigate('/auth')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-[13px] font-medium transition-all active:scale-[0.98] bg-overlay text-emerald-500 hover:bg-emerald-500/10 border border-emerald-500/20 font-sora"
              data-testid="sign-in-btn">
              <UserPlus className="w-4 h-4" strokeWidth={1.5} /> Sign in to save chats
            </button>
          ) : (
            <div className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-content-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 bg-emerald-500/15 text-emerald-500 font-mono">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="truncate flex-1 font-sora">{user?.name}</span>
              <button onClick={onLogout} className="p-1.5 rounded-lg hover:bg-overlay-hover text-content-3 hover:text-red-400 transition-colors" data-testid="logout-btn">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
          <button onClick={() => navigate('/')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-all w-full active:scale-[0.98] font-sora" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to OpenSov
          </button>
        </div>
      </div>
    </aside>
  );
}
